<template>
  <div class="wrapper">

    <div v-if="isProcessingVisible" class="modal-overlay">
      <div class="modal-content">
        <p>{{ t('childEdit.processing') }}</p>
        <div class="spinner"></div>
      </div>
    </div>

    <div class="header_wrapper">
      <v-toolbar color="primary" dark style="position: relative;">
        <v-btn @click="routerBack" icon>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title
          style="
            position: absolute;
            left: 50%;
            margin: 0;
            padding: 0;
            max-width: 70%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          "
          class="text-center child-edit-title"
        >
          {{ lastName + firstName }}
        </v-toolbar-title>

        <div style="width: 70px; margin-left: auto;">
          <v-btn
            @click="putChildren"
            :loading="btnLoading"
            variant="text"
            class="no-background-hover font-weight-bold"
            icon
          >
            {{ t('childEdit.save') }}
          </v-btn>
        </div>
      </v-toolbar>
    </div>

    <v-form
      ref="put_children_form"
      class="px-2 py-3"
    >

      <div class="d-flex justify-center mb-5">
        <div
          v-show="pickedImg"
          class="picked_img"
          @click="pickFile"
        >
          <img
            :src="resolveChildImageUrl(pickedImg) || undefined"
          />
        </div>
        <div
          v-show="!pickedImg"
          class="file_uploader pa-3"
          for="corporation_file"
          :class="[{'-drag': isDrag == 'new'}]"
          @click="pickFile"
          @dragover.prevent="checkDrag($event, 'new', true)"
          @dragleave.prevent="checkDrag($event, 'new', false)"
          @drop.prevent="onDrop"
        >
          <input
            class="d-none"
            id="file-input"
            ref="fileInput"
            type="file"
            @change="onDrop"
            accept=".png,.jpg,image/jpeg,image/png"
          />
        </div>
      </div>

      <div class="d-flex">
        <div style="width: 50%;" class="pr-1">
          <p class="body-2 blue-grey--text">{{ t('childEdit.lastName') }}</p>
          <v-text-field v-model="lastName" variant="outlined" class="large-text-field" :rules="[rules.required]"></v-text-field>
        </div>

        <div style="width: 50%;" class="pr-1">
          <p class="body-2 blue-grey--text">{{ t('childEdit.firstName') }}</p>
          <v-text-field v-model="firstName" variant="outlined" class="large-text-field" :rules="[rules.required]"></v-text-field>
        </div>
      </div>

      <div v-if="false" class="d-flex">
        <div style="width: 50%;" class="pr-1">
          <p class="body-2 blue-grey--text">{{ t('childEdit.lastNameKana') }}</p>
          <v-text-field
            v-model="lastNameKana"
            variant="outlined"
            class="large-text-field"
          ></v-text-field>
        </div>

        <div style="width: 50%;" class="pr-1">
          <p class="body-2 blue-grey--text">{{ t('childEdit.firstNameKana') }}</p>
          <v-text-field
            v-model="firstNameKana"
            variant="outlined"
            class="large-text-field"
          ></v-text-field>
        </div>
      </div>

    </v-form>

  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
dayjs.locale('ja');
import Swal from 'sweetalert2';
import { Toast } from '@/service/SweetAlert';
import rules from '@/service/ValidRules';

import { meModule } from '@/store/entityModules/me';
import { familyModule } from '../store/entityModules/family';
import { childrenModule } from '@/store/entityModules/children';
import { resourceModule } from '@/store/entityModules/resource';
import { t } from '@/i18n';
import { resolveChildImageUrl } from '@/utils/childImageUrl';

import type { VForm } from 'vuetify/components';
import { ChildInfoFormat } from 'chaild-entity/src/component/nursery/child/types';

interface DataType {
  childId: string;
  imageFile: File | null;
  pickedImg: string;
  isDrag: string;
  firstName: string;
  lastName: string;
  firstNameKana: string | null;
  lastNameKana: string | null;
  imageUrl: string | null;
  btnLoading: boolean;
  isProcessingVisible: boolean;
}

export default defineComponent({

  computed: {
    rules() {
      return rules;
    },
    form() {
      return this.$refs.put_children_form as any;
    },
  },

  watch: {
    isBirthPicker(val) {
      setTimeout(() => ((this.$refs as any).picker.activePicker = 'YEAR'));
    },
  },

  data: (): DataType =>({
    childId: '',
    imageFile: null,
    pickedImg: '',
    isDrag: '',
    firstName: '',
    lastName: '',
    firstNameKana: null,
    lastNameKana: null,
    imageUrl: null,
    btnLoading: false,
    isProcessingVisible: false,
  }),

  methods: {
    t,
    resolveChildImageUrl,
    routerBack() {
      this.$router.replace('/setting/family');
    },
    onImagePicked(uploadImageUrl: File | null) {
      this.imageFile = uploadImageUrl;
    },
    pickFile() {
      const fileInput = this.$refs.fileInput as HTMLInputElement;
      fileInput.click();
    },
    checkDrag(event: any, key: string, status: boolean) {
      if (status && event.dataTransfer.types === 'text/plain') {
        return false;
      }
      this.isDrag = status ? key : '';
    },
    async onDrop(event: any, key = '', image = {}) {
      this.isDrag = '';
      const fileList = event.target.files ? event.target.files : event.dataTransfer.files;
      if (fileList.length === 0) {
        return false;
      }
      this.isProcessingVisible = true;
      const file = fileList[0];
      const fr = new FileReader();
      fr.readAsDataURL(file);
      fr.addEventListener('load', () => {
        this.pickedImg = fr.result as string;
      });
      const response = await resourceModule.postResource({
        imageType: 'childProfile',
        contentType: file.contentType,
        file,
        childId: this.childId,
      });
      if (response) {
        const url = response.split('?')[0];
        this.imageUrl = url;
        this.imageFile = file;
      }
      this.isProcessingVisible = false;
    },
    async putChildren() {
      const { valid } = await this.form.validate();
      if (valid) {
        try {
          this.btnLoading = true;
          const response = await childrenModule.putChildren({
            childId: this.childId,
            firstName: this.firstName,
            lastName: this.lastName,
            firstNameKana: this.firstNameKana,
            lastNameKana: this.lastNameKana,
            imageUrl: this.imageUrl,
          });
          Toast.fire({
            icon: 'success',
            title: this.t('childEdit.successMessage'),
          });
          this.btnLoading = false;
        } catch (error) {
          this.btnLoading = false;
        }
      }
    },
  },

  async mounted() {
    if (typeof this.$route.query.childId === 'string') {
      this.childId = String(this.$route.query.childId);
      if (!familyModule.family && meModule.me) {
        familyModule.fetchFamilyById({
          familyId: meModule.me.families[0].familyId,
        });
      }
      if (familyModule.family) {
        familyModule.family.children.map((c) => {
          if (c.childId === Number(this.childId)) {
            this.firstName = c.firstName;
            this.lastName = c.lastName;
            this.firstNameKana = c.firstNameKana;
            this.lastNameKana = c.lastNameKana;
            this.imageUrl = c.imageUrl;
            if (c.imageUrl) {
              this.pickedImg = c.imageUrl;
            }
          }
        });
      }
    }
  },

});
</script>
<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
}

/* スピナー */
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #333;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 10px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===== レイアウト ===== */
.wrapper {
  padding-top: calc(var(--app-top-offset) + 56px); // ← ヘッダー分
  margin-bottom: calc(env(safe-area-inset-bottom) + 56px);
}

/* ===== ヘッダー ===== */
.header_wrapper {
  width: 100%;
  position: fixed;
  top: var(--app-top-offset); // ← 超重要
  left: 0;
  z-index: 100;

  .outline {
    border: 1px solid rgb(var(--v-theme-primary));
  }
}

.child-edit-title {
  transform: translateX(-50%);
}

@media (max-width: 430px) {
  .child-edit-title {
    transform: translateX(-55%);
  }
}

/* ===== フォーム余白 ===== */
.v-form {
  margin-top: 8px;
}

/* ===== ラベルサイズ ===== */
.label-size > .v-input__control > .v-input__slot > .v-input--selection-controls__input > label {
  font-size: 12px;
}

/* ===== 画像 ===== */
.picked_img {
  height: 100px;
  width: 100px;
  background-color: #d5d5d5;
  border-radius: 100%;

  img {
    object-fit: cover;
    border-radius: 100%;
    height: 100%;
    width: 100%;
  }
}

/* ===== アップローダー ===== */
.file_uploader {
  height: 100px;
  width: 100px;
  background-color: #d5d5d5;
  border-radius: 100%;
}
.label-size > .v-input__control > .v-input__slot > .v-input--selection-controls__input > label {
  font-size: 12px;
}
.picked_img {
  height: 100px;
  width: 100px;
  background-color: #d5d5d5;
  border-radius: 100%;
  img {
    object-fit: cover;
    border-radius: 100%;
    height: 100%;
    width: 100%;
  }
}
.file_uploader {
  height: 100px;
  width: 100px;
  background-color: #d5d5d5;
  border-radius: 100%;
}
</style>
